import React, { useId } from 'react';
import {
  ComposedChart, BarChart, Area, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ReferenceDot, ReferenceArea,
} from 'recharts';
import {
  niceScale, tickFormatter, dateTicks, fmtDay, fmtSpan, fmtInt, fmtSigned, fmtRate, nullRuns, changeBars, WEEK_TICK_STEPS,
  DAY_TICK_STEPS,
} from '../../lib/slotStatsView';
import { AxisFrame, TooltipBox, DataEndBar, DeltaMark, AXIS_TICK, yAxisWidth } from './ChartKit';

/*
 * Round 14b: the account-growth charts. One measure per chart (no dual axes): the follower count
 * over time (with the projection as a dashed, labelled estimate after the last check), following
 * and posts as their own small multiples, and the follower change as diverging bars (gain blue,
 * loss red, a neutral grey zero line) - weekly bars for the 90-day range.
 *
 * Lines join the once-a-day samples with STRAIGHT segments (a smoothed curve invents values
 * between checks); posts step, because a post count changes in whole jumps.
 */

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

const safeId = (id) => `viz${String(id).replace(/[^a-zA-Z0-9_-]/g, '')}`;

/** How many date labels fit: about one per 84px, never more than 8. */
export const maxDateTicks = (width) => Math.max(2, Math.min(8, Math.floor(width / 84)));

/**
 * The shaded "not recorded" stretches of a chart (drawn under the marks). Labelled when the stretch
 * is wide enough to hold the words; the tooltip says it on every day either way.
 */
export function notRecordedAreas(runs, width, totalDays) {
  return runs.map((r) => (
    <ReferenceArea key={`nr-${r.from}`} x1={r.from} x2={r.to} fill="var(--viz-hover)" fillOpacity={1} stroke="none"
      ifOverflow="hidden"
      label={(width * r.days) / Math.max(1, totalDays) >= 84
        ? { value: 'Not recorded', position: 'insideTop', fill: 'var(--viz-ink-3)', fontSize: 11, offset: 8 }
        : undefined} />
  ));
}

const ACTIVE_DOT = { r: 5, stroke: 'var(--viz-surface)', strokeWidth: 2, fill: 'var(--viz-line)' };
const DOT = { r: 4, stroke: 'var(--viz-surface)', strokeWidth: 2, fill: 'var(--viz-line)' };

function EndLabel({ viewBox, text, chartWidth, leftOfPoint }) {
  if (!viewBox) return null;
  const cx = viewBox.x + (viewBox.width || 0) / 2;
  const cy = viewBox.y + (viewBox.height || 0) / 2;
  const left = leftOfPoint || cx > chartWidth - 72;
  return (
    <text x={left ? cx - 7 : cx} y={cy - 11} textAnchor={left ? 'end' : 'middle'}
      className="viz-end-label" fill="var(--viz-ink)" fontSize={12} fontWeight={600}>
      {text}
    </text>
  );
}

function GrowthTip({ active, payload, label, mode, noun, firstDay, proj }) {
  if (!active) return null;
  const d = payload && payload[0] ? payload[0].payload : null;
  const date = d ? d.date : label;
  if (!date) return null;
  const title = fmtDay(date, 'long');
  if (d && d.future) {
    return (
      <TooltipBox title={title}
        rows={[{ key: 'p', color: 'var(--viz-line)', value: fmtInt(d.proj), label: `${noun} (estimate)` }]}
        footer={proj ? `At ${fmtRate(proj.perDay)} a day, the pace of the last ${proj.windowDays} recorded days. An estimate, not a forecast.` : null} />
    );
  }
  const hasValue = d && isNum(d.v);
  if (!hasValue && !(d && isNum(d.count))) {
    return <TooltipBox title={title} rows={[{ key: 'nr', value: 'Not recorded', label: 'no profile check that day' }]} />;
  }
  const rows = [];
  if (mode === 'count') rows.push({ key: 'v', color: 'var(--viz-line)', value: fmtInt(d.count), label: `${noun} at the day's check` });
  else rows.push({ key: 'v', color: 'var(--viz-line)', value: fmtSigned(d.v), label: `net from ${fmtDay(firstDay)}` });
  if (d.change === null) {
    rows.push({ key: 'c', value: '-', label: d.seed ? 'first profile check: no earlier count' : 'change not recorded' });
  } else {
    rows.push({ key: 'c', mark: <DeltaMark value={d.change} />, value: fmtSigned(d.change), label: 'since the previous check' });
  }
  return <TooltipBox title={title} rows={rows} />;
}

/**
 * A growth line over the range: the count at each profile check (mode 'count'), or - with no exact
 * base count - the running net change since the range started (mode 'net', zero baseline).
 * `curve`: 'linear' or 'stepAfter'. `proj`: { line:[{date, proj}], perDay, windowDays } draws the
 * estimate after the last check as a dashed line in a labelled band (count mode only).
 */
export function GrowthChart({ gp, noun, yTitle, height = 260, curve = 'linear', proj = null, testid }) {
  const gid = safeId(useId());
  const mode = gp.mode === 'count' ? 'count' : 'net';
  const real = gp.points.map((p) => ({
    date: p.date, v: mode === 'count' ? p.count : p.net, change: p.change, count: p.count, seed: p.seed, proj: null,
  }));
  const last = [...real].reverse().find((d) => isNum(d.v));
  if (!last) return null;
  const withProj = mode === 'count' && proj && proj.line.length > 0 && last.date === real[real.length - 1].date;
  const data = withProj
    ? real.map((d) => (d === last ? { ...d, proj: d.v } : d)).concat(proj.line.map((p) => ({
      date: p.date, v: null, change: null, count: null, seed: false, proj: Math.round(p.proj), future: true,
    })))
    : real;
  const vals = data.flatMap((d) => [d.v, d.proj]).filter(isNum);
  const scale = niceScale(Math.min(...vals), Math.max(...vals), { count: height < 220 ? 4 : 5, zero: mode === 'net' });
  const fmt = tickFormatter(scale.step, Math.max(Math.abs(scale.lo), Math.abs(scale.hi)));
  const firstDay = (real.find((d) => isNum(d.v)) || {}).date;
  const days = data.map((d) => d.date);
  // the latest value at the line's end, exact: never less precise than the axis
  const endText = mode === 'count' ? fmtInt(last.v) : fmtSigned(last.v);
  const runs = nullRuns(real, (d) => {
    if (isNum(d.v)) return d.v;
    return isNum(d.count) ? d.count : null;
  });
  const future = withProj ? data.filter((d) => d.future) : [];
  return (
    <AxisFrame yTitle={yTitle} testid={testid}>
      {(width) => {
        // sample dots only where they stay apart (packed dots read as a dashed line, i.e. an estimate)
        const dots = real.length <= 31 && width / Math.max(1, data.length) >= 20;
        return (
          <ComposedChart width={width} height={height} data={data} margin={{ top: 24, right: 24, bottom: 0, left: 0 }}
            role="img" aria-label={`${yTitle} by day, ${fmtDay(days[0], 'medium')} to ${fmtDay(real[real.length - 1].date, 'medium')}`
              + `${withProj ? `, then an estimate to ${fmtDay(days[days.length - 1], 'medium')}` : ''}`}>
            <defs>
              <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--viz-line)" stopOpacity={0.18} />
                <stop offset="100%" stopColor="var(--viz-line)" stopOpacity={0.01} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke="var(--viz-grid)" />
            {notRecordedAreas(runs, width, days.length)}
            {future.length > 0 && (
              <ReferenceArea x1={future[0].date} x2={future[future.length - 1].date} fill="var(--viz-hover)" fillOpacity={1}
                stroke="none" ifOverflow="hidden"
                label={{ value: 'Estimate', position: 'insideTop', fill: 'var(--viz-ink-3)', fontSize: 11, offset: 8 }} />
            )}
            <XAxis dataKey="date" ticks={dateTicks(days, maxDateTicks(width))} interval={0} tickFormatter={(d) => fmtDay(d)}
              tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: 'var(--viz-axis)' }} tickMargin={8} />
            <YAxis type="number" domain={[scale.lo, scale.hi]} ticks={scale.ticks} tickFormatter={fmt}
              width={yAxisWidth(scale.ticks, fmt)} tick={AXIS_TICK} tickLine={false} axisLine={false} tickMargin={6}
              allowDecimals={false} allowDataOverflow />
            {mode === 'net' && <ReferenceLine y={0} stroke="var(--viz-axis)" />}
            <Tooltip filterNull={false} isAnimationActive={false} cursor={{ stroke: 'var(--viz-ink-3)', strokeWidth: 1 }}
              content={(p) => <GrowthTip {...p} mode={mode} noun={noun} firstDay={firstDay} proj={proj} />} />
            <Area type={curve} dataKey="v" name={noun} baseValue={mode === 'count' ? scale.lo : 0}
              stroke="var(--viz-line)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round"
              fill={`url(#${gid})`} connectNulls={false} isAnimationActive={false}
              dot={dots ? DOT : false} activeDot={ACTIVE_DOT} />
            {withProj && (
              <Line type="linear" dataKey="proj" name={`${noun} (estimate)`} stroke="var(--viz-line)" strokeWidth={2}
                strokeDasharray="5 4" strokeOpacity={0.75} dot={false} activeDot={{ ...ACTIVE_DOT, fillOpacity: 0.75 }}
                connectNulls={false} isAnimationActive={false} />
            )}
            <ReferenceDot x={last.date} y={last.v} r={4} fill="var(--viz-line)" stroke="var(--viz-surface)" strokeWidth={2}
              ifOverflow="visible" label={<EndLabel text={endText} chartWidth={width} leftOfPoint={withProj} />} />
          </ComposedChart>
        );
      }}
    </AxisFrame>
  );
}

function ChangeTip({ active, payload, label, noun, weekly }) {
  if (!active) return null;
  const d = payload && payload[0] ? payload[0].payload : null;
  const date = d ? d.date : label;
  if (!date) return null;
  const title = weekly && d
    ? `${fmtSpan(d.from, d.to)}${d.recorded < 7 ? ` · ${d.recorded} of 7 days recorded` : ''}`
    : fmtDay(date, 'long');
  if (!d || d.change === null) {
    return (
      <TooltipBox title={title} rows={[{
        key: 'nr', value: 'Not recorded',
        label: d && d.seed ? 'first profile check: nothing to compare with' : (weekly ? 'no profile check that week' : 'no profile check that day'),
      }]} />
    );
  }
  const word = d.change > 0 ? 'gained' : d.change < 0 ? 'lost' : 'no change';
  const rows = [{
    key: 'c',
    color: d.change > 0 ? 'var(--viz-gain)' : d.change < 0 ? 'var(--viz-loss)' : null,
    value: fmtSigned(d.change),
    label: d.change === 0 ? word : `${noun} ${word}`,
  }];
  if (isNum(d.count)) rows.push({ key: 'n', value: fmtInt(d.count), label: `${noun} at the ${weekly ? 'week\'s last' : 'day\'s'} check` });
  return <TooltipBox title={title} rows={rows} />;
}

/** Follower change as diverging bars from a zero baseline: one per day, or one per week (`weekly`). */
export function DailyChangeChart({ gp, noun, yTitle, height = 240, weekly = false, testid }) {
  const data = changeBars(gp.points, weekly).map((b) => ({ ...b, partial: weekly && b.recorded > 0 && b.recorded < 7 }));
  const vals = data.map((d) => d.change).filter(isNum);
  if (!vals.length) return null;
  const scale = niceScale(Math.min(0, ...vals), Math.max(0, ...vals), { count: 5, zero: true });
  const fmt = tickFormatter(scale.step, Math.max(Math.abs(scale.lo), Math.abs(scale.hi)));
  const days = data.map((d) => d.date);
  const colorOf = (v) => (v >= 0 ? 'var(--viz-gain)' : 'var(--viz-loss)');
  const runs = nullRuns(data, (d) => d.change);
  return (
    <AxisFrame yTitle={yTitle} xTitle={weekly ? 'Week ending' : 'Date'} testid={testid}>
      {(width) => (
        <BarChart width={width} height={height} data={data} margin={{ top: 12, right: 24, bottom: 0, left: 0 }}
          barCategoryGap={weekly ? '28%' : '18%'} role="img"
          aria-label={`${yTitle}, ${fmtDay(data[0].from, 'medium')} to ${fmtDay(days[days.length - 1], 'medium')}`}>
          <CartesianGrid vertical={false} stroke="var(--viz-grid)" />
          {notRecordedAreas(runs, width, days.length)}
          <XAxis dataKey="date" ticks={dateTicks(days, maxDateTicks(width), weekly ? WEEK_TICK_STEPS : DAY_TICK_STEPS)} interval={0}
            tickFormatter={(d) => fmtDay(d)} tick={AXIS_TICK} tickLine={false} axisLine={false} tickMargin={8} />
          <YAxis type="number" domain={[scale.lo, scale.hi]} ticks={scale.ticks} tickFormatter={(v) => (v > 0 ? `+${fmt(v)}` : fmt(v))}
            width={yAxisWidth(scale.ticks, (v) => `+${fmt(v)}`)} tick={AXIS_TICK} tickLine={false} axisLine={false} tickMargin={6}
            allowDataOverflow />
          <Tooltip filterNull={false} isAnimationActive={false} cursor={{ fill: 'var(--viz-hover)' }}
            content={(p) => <ChangeTip {...p} noun={noun} weekly={weekly} />} />
          <Bar dataKey="change" name={noun} maxBarSize={24} isAnimationActive={false}
            shape={(p) => <DataEndBar {...p} colorOf={colorOf} />}
            activeBar={(p) => <DataEndBar {...p} colorOf={colorOf} isActive />} />
          <ReferenceLine y={0} stroke="var(--viz-axis)" strokeWidth={1} />
        </BarChart>
      )}
    </AxisFrame>
  );
}
